import { Tool } from '@procureai/agents';
import { ToolLoading, ToolResult } from '@procureai/uix/ai';
import { useTranslation } from 'react-i18next';

import { SvbrgSourcerPurchaseRequisitionInput, SvbrgSourcerPurchaseRequisitionOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { useInternalSourcerResultDataGrid } from '@/legacy/routes/IntakeChat/components/InternalSourcerResult/InternalSourcerResult.dataGrid';

import { ToolResultComponent } from '.';
import { ResultsTableCard } from './components/ResultsTableCard';

export const AskInternalSourcerTool: ToolResultComponent<
  Tool.askInternalSourcer,
  SvbrgSourcerPurchaseRequisitionInput,
  SvbrgSourcerPurchaseRequisitionOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const config = useInternalSourcerResultDataGrid();

  switch (tool.state) {
    case 'input-streaming':
    case 'input-available':
      return (
        <ToolLoading
          label={t(`call`, {
            description: tool.input?.searchQuery,
          })}
        />
      );

    case 'output-available': {
      const count =
        tool.output.suppliersByContracts?.length +
          tool.output.suppliersByPurchaseOrders?.length +
          tool.output.suppliersBySourcingProjects?.length || 0;
      const hasResults = count > 0;
      // The row type for the DataGrid is SvbrgSupplierFindings (from the contract/PO/project arrays)
      type RowType = typeof tool.output.suppliersByContracts extends (infer U)[] ? U : never;
      const suppliers: { title: string; row: RowType[] }[] = [
        {
          title: t('resultSection.contracts'),
          row: tool.output.suppliersByContracts || [],
        },
        {
          title: t('resultSection.purchaseOrders'),
          row: tool.output.suppliersByPurchaseOrders || [],
        },
        {
          title: t('resultSection.sourcingProjects'),
          row: tool.output.suppliersBySourcingProjects || [],
        },
      ].filter((group) => group.row.length > 0);
      const description = tool.output.filterSummary || '';

      return (
        <ToolResult
          label={t(`result`, {
            count,
            description,
          })}
          defaultOpen={false}
        >
          {/* Results table section - only show if there are results */}
          {hasResults && <ResultsTableCard<RowType> results={suppliers} config={config} />}
        </ToolResult>
      );
    }
  }
};
